import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/shared/components/Icons/Icons';

interface NotificationDeviceOption {
  name: string;
  label: string;
}

interface NotificationOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  deviceFilterName: string;
  deviceOptions: NotificationDeviceOption[];
  onDeviceFilterNameChange: (value: string) => void;
  onMarkAllAsRead: () => void;
  isMarkAllAsReadPending: boolean;
}

export function NotificationOptionsDialog({
  open,
  onClose,
  deviceFilterName,
  deviceOptions,
  onDeviceFilterNameChange,
  onMarkAllAsRead,
  isMarkAllAsReadPending,
}: NotificationOptionsDialogProps) {
  const { t } = useTranslation();

  const handleDeviceSelectChange = (event: SelectChangeEvent<string>) => {
    onDeviceFilterNameChange(event.target.value);
  };

  const getDeviceFilterLabel = (value: string) => {
    if (!value) {
      return t('notifications.filters.all');
    }

    return (
      deviceOptions.find((option) => option.name === value)?.label ?? value
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 5, px: 6 }}>
        <Typography sx={{ fontWeight: 600, flex: 1, fontSize: '22px' }}>
          {t('notifications.options.title')}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('notifications.options.close')}
        >
          <CloseIcon fontSize="small" sx={{ color: 'primary.dark' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, px: 6, pb: 6 }}>
        <Typography
          variant="body2"
          sx={{ color: 'primary.dark', mb: 2, fontWeight: 500 }}
        >
          {t('notifications.options.filterByCcu')}
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={deviceFilterName}
            displayEmpty
            onChange={handleDeviceSelectChange}
            renderValue={getDeviceFilterLabel}
          >
            <MenuItem value="">{t('notifications.filters.all')}</MenuItem>
            {deviceOptions.map((option) => (
              <MenuItem key={option.name} value={option.name}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Divider sx={{ my: 6 }} />
        <Button
          variant="outlined"
          fullWidth
          disabled={isMarkAllAsReadPending}
          onClick={onMarkAllAsRead}
        >
          {t('notifications.options.markAllAsRead')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
